import React from 'react';
import { useTranslation } from 'react-i18next';

interface MilestoneCardProps {
  milestone: any;
  onEdit: (milestone: any) => void;
  onDragStart?: (milestone: any) => void;
  isSelected?: boolean;
  onSelect?: (checked: boolean) => void;
  canEdit?: boolean;
  user?: { organizationRole?: string };
}

export function MilestoneCard({ 
  milestone, 
  onEdit, 
  onDragStart, 
  isSelected, 
  onSelect, 
  canEdit = true,
  user
}: MilestoneCardProps) {
  const { t } = useTranslation();
  const isInstructor = user?.organizationRole === 'instructor';
  const isSocialWorker = user?.organizationRole === 'social_worker';
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'achieved': return '#28a745';
      case 'in_progress': return '#007bff';
      case 'frozen': return '#6c757d';
      case 'maintenance': return '#17a2b8';
      case 'stuck': return '#dc3545';
      case 'new': return '#007acc';
      default: return '#6c757d';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'new': return t('patientDetail.statusNew');
      case 'in_progress': return t('patientDetail.statusInProgress');
      case 'achieved': return t('patientDetail.statusAchieved');
      case 'frozen': return t('patientDetail.statusFrozen');
      case 'maintenance': return t('patientDetail.statusMaintenance');
      case 'stuck': return t('patientDetail.statusStuck');
      default: return status;
    }
  };

  return (
    <div
      onClick={() => canEdit && onEdit(milestone)}
      style={{
        background: 'white',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        padding: '15px',
        marginBottom: '12px',
        cursor: canEdit ? 'pointer' : 'default',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        transition: 'all 0.2s ease',
        minWidth: 0,
        overflow: 'hidden',
        opacity: canEdit ? 1 : 0.7
      }}
      onMouseEnter={(e) => {
        if (canEdit) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
        }
      }}
      onMouseLeave={(e) => {
        if (canEdit) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
        }
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
          {onSelect && !isInstructor && (
            <input
              type="checkbox"
              checked={isSelected || false}
              disabled={isSocialWorker && isSelected}
              onChange={(e) => {
                e.stopPropagation();
                // For social workers, prevent unchecking
                if (isSocialWorker && !e.target.checked) {
                  return;
                }
                onSelect(e.target.checked);
              }}
              onClick={(e) => {
                e.stopPropagation();
                // For social workers, prevent unchecking
                if (isSocialWorker && isSelected) {
                  e.preventDefault();
                  return;
                }
              }}
              style={{ 
                margin: 0,
                cursor: (isSocialWorker && isSelected) ? 'not-allowed' : 'pointer',
                transform: 'scale(1.1)',
                flexShrink: 0,
                opacity: (isSocialWorker && isSelected) ? 0.6 : 1
              }}
              title={isSocialWorker && isSelected ? (t('patientDetail.cannotUncheckMilestone') || 'Cannot uncheck - milestone is achieved') : ''}
            />
          )}
          <h5 style={{ 
            margin: 0, 
            fontSize: '14px', 
            fontWeight: 'bold', 
            color: '#000000',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0
          }}>
            {milestone.title}
          </h5>
        </div>
        {!canEdit && (
          <span style={{ 
            fontSize: '11px', 
            color: '#999', 
            fontStyle: 'italic',
            marginLeft: '8px',
            flexShrink: 0
          }}>
            🔒 {t('patientDetail.readOnly') || 'Read only'}
          </span>
        )}
      </div>

      {milestone.description && (
        <div style={{ 
          fontSize: '12px', 
          color: '#000000',
          marginBottom: '10px',
          lineHeight: '1.4',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}>
          <span style={{ color: '#000000' }}>
            {milestone.description}
          </span>
        </div>
      )}

      <div style={{ marginBottom: '10px' }}>
        <div style={{ display: 'flex', gap: '2px', marginBottom: '10px' }}>
          {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((value) => (
            <div
              key={value}
              style={{
                flex: 1,
                height: '30px',
                backgroundColor: milestone.progress >= value ? '#007acc' : '#e9ecef',
                cursor: 'default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: milestone.progress >= value ? 'white' : '#000000',
                borderRadius: value === 0 ? '4px 0 0 4px' : value === 100 ? '0 4px 4px 0' : '0',
                transition: 'background-color 0.2s ease'
              }}
            >
              {value}
            </div>
          ))}
        </div>
        <div style={{ fontSize: '14px', color: '#000000', textAlign: 'center' }}>
          {t('patientDetail.currentProgress')}: {milestone.progress || 0}%
        </div>
      </div>

      {milestone.successMetric && (
        <div style={{ 
          fontSize: '11px', 
          color: '#000000', 
          marginBottom: '6px',
          fontWeight: 'bold',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          🎯 {milestone.successMetric}
        </div>
      )}

      {milestone.resources && (
        <div style={{ 
          fontSize: '11px', 
          color: '#000000', 
          marginBottom: '6px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          💪 {milestone.resources}
        </div>
      )}

      {!isInstructor && (milestone.internalBarriers || milestone.externalBarriers || milestone.barriers) && (
        <div style={{ 
          fontSize: '11px', 
          color: '#000000', 
          marginBottom: '6px'
        }}>
          {milestone.internalBarriers && (
            <div style={{ marginBottom: '4px' }}>
              <span style={{ fontWeight: '600', color: '#6c757d' }}>פנימיים:</span> {milestone.internalBarriers}
            </div>
          )}
          {milestone.externalBarriers && (
            <div style={{ marginBottom: '4px' }}>
              <span style={{ fontWeight: '600', color: '#6c757d' }}>חיצוניים:</span> {milestone.externalBarriers}
            </div>
          )}
          {!milestone.internalBarriers && !milestone.externalBarriers && milestone.barriers && (
            <div>⚠️ {milestone.barriers}</div>
          )}
        </div>
      )}

      <div 
        style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '10px',
          paddingTop: '8px',
          borderTop: '1px solid #e9ecef'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            color: '#000000',
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          {milestone.targetDate ? (
            <>
              📅 {new Date(milestone.targetDate).toLocaleDateString('he-IL')}
            </>
          ) : milestone.createdAt ? (
            <>
              📅 {new Date(milestone.createdAt).toLocaleDateString('he-IL')}
            </>
          ) : (
            <span style={{ color: '#000000' }}>אין תאריך</span>
          )}
        </div>

        <span
          draggable={canEdit}
          onDragStart={(e) => {
            e.stopPropagation();
            if (!canEdit) {
              e.preventDefault();
              return;
            }
            if (onDragStart) {
              onDragStart(milestone);
            }
            e.dataTransfer.setData('text/plain', JSON.stringify(milestone));
            e.dataTransfer.effectAllowed = 'move';
          }}
          onDragEnd={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
          style={{
            color: '#999',
            fontSize: '14px',
            cursor: canEdit ? 'grab' : 'not-allowed',
            padding: '4px 8px',
            borderRadius: '4px',
            transition: 'all 0.2s ease',
            userSelect: 'none',
            opacity: canEdit ? 1 : 0.5
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f8f9fa';
            e.currentTarget.style.color = '#666';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#999';
          }}
          title="גרור לעמודה אחרת"
        >
          ⋮⋮
        </span>

        <span
          style={{
            padding: '3px 8px',
            borderRadius: '12px',
            fontSize: '11px',
            background: getStatusColor(milestone.status),
            color: 'white',
            fontWeight: 'bold'
          }}
        >
          {getStatusLabel(milestone.status)}
        </span>
      </div>
    </div>
  );
}

